"use client";
import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const BonusTypeContext = createContext();

export const BonusTypeProvider = ({ children }) => {
  const [bonusTypes, setBonusTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ Get token
  const getToken = () => localStorage.getItem("token");

  // ✅ Safely parse JSON
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ✅ Fetch all bonus types
  const fetchBonusTypes = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/bonus-types`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch bonus types");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setBonusTypes(data.data || []);
      } else {
        setError(data.message || "Failed to fetch bonus types");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching bonus types");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Create bonus type
  const createBonusType = async (payload) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/bonus-types`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to create bonus type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchBonusTypes();
        toast.success(data.message || "Bonus type created successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create bonus type",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error creating bonus type",
      };
    } finally {
      setLoading(false);
    }
  };

  // ✅ Update bonus type
  const updateBonusType = async (id, payload) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/bonus-types/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update bonus type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchBonusTypes();
        toast.success(data.message || "Bonus type updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update bonus type",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating bonus type",
      };
    } finally {
      setLoading(false);
    }
  };

  // ✅ Update status
  const updateBonusTypeStatus = async (id, status, update_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/bonus-types/${id}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status, update_audit_id }),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchBonusTypes();
        toast.success(data.message || "Status updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update status",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating status",
      };
    }
  };

  // ✅ Delete bonus type (soft delete)
  const deleteBonusType = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/bonus-types/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id }),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to delete bonus type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchBonusTypes();
        toast.success(data.message || "Bonus type deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete bonus type",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error deleting bonus type",
      };
    }
  };

  // ✅ Load on mount
  useEffect(() => {
    const token = getToken();
    if (token) {
      fetchBonusTypes();
    }
  }, []);

  return (
    <BonusTypeContext.Provider
      value={{
        bonusTypes,
        loading,
        error,
        fetchBonusTypes,
        createBonusType,
        updateBonusType,
        updateBonusTypeStatus,
        deleteBonusType,
      }}
    >
      {children}
    </BonusTypeContext.Provider>
  );
};

// ✅ Hook
export const useBonusTypes = () => useContext(BonusTypeContext);